<template>
  <div>
    <section class="container page-top hero">
      <span class="pill">{{ page.hero.eyebrow }}</span>
      <h1 class="h-title">{{ page.hero.headline }}</h1>
      <p class="lead">{{ page.hero.body }}</p>
      <p v-if="isAr" class="note">{{ ui('language_note') }}</p>
    </section>

    <section v-if="featuredPost" class="section container">
      <router-link :to="to(`/blog/${featuredPost.slug}`)" class="featured">
        <div class="f-image">
          <ImageSlot :src="featuredPost.image || ''" :alt="featuredPost.title" :label="featuredPost.category" :tint="tints[0]" />
        </div>
        <div class="f-content">
          <span class="tag">{{ featuredPost.category }}</span>
          <span class="meta">{{ featuredPost.date }} · {{ featuredPost.readTime }}</span>
          <h2 class="card-title f-title">{{ featuredPost.title }}</h2>
          <p class="excerpt">{{ featuredPost.excerpt }}</p>
          <span class="more">{{ ui('read_article') }} {{ arrow }}</span>
        </div>
      </router-link>
    </section>

    <section v-if="regularPosts.length" class="container grid-section">
      <div class="grid">
        <router-link v-for="(post, index) in regularPosts" :key="post.id" :to="to(`/blog/${post.slug}`)" class="post">
          <div class="p-image">
            <ImageSlot :src="post.image || ''" :alt="post.title" :label="post.category" :tint="tints[(index + 1) % tints.length]" />
          </div>
          <span class="tag">{{ post.category }}</span>
          <span class="meta">{{ post.date }} · {{ post.readTime }}</span>
          <h3 class="card-title p-title">{{ post.title }}</h3>
          <p class="excerpt">{{ post.excerpt }}</p>
          <span class="more">{{ ui('read_article') }} {{ arrow }}</span>
        </router-link>
      </div>
    </section>

    <CtaBand />
  </div>
</template>

<script setup>
import { computed } from 'vue'
import ImageSlot from '../components/ImageSlot.vue'
import CtaBand from '../components/CtaBand.vue'
import { useLocale } from '../composables/useLocale'
import { blogPosts, pages } from '../data'

const page = pages.blog
const { isAr, ui, arrow, to } = useLocale()

const featuredPost = computed(() => blogPosts.find((post) => post.featured) || blogPosts[0])
const regularPosts = computed(() => blogPosts.filter((post) => post !== featuredPost.value))

const tints = ['var(--tint-1)', 'var(--tint-2)', 'var(--tint-3)', 'var(--tint-4)']
</script>

<style scoped>
.hero {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
}

.hero .lead {
  max-width: 56ch;
}

.featured {
  background: var(--surface);
  border-radius: var(--radius-card);
  padding: 14px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 420px), 1fr));
  gap: clamp(20px, 3vw, 40px);
  color: var(--ink);
  transition: background 0.2s ease;
}

.featured:hover {
  background: var(--surface-hover);
  color: var(--ink);
}

.f-image {
  min-height: 260px;
  border-radius: 16px;
  overflow: hidden;
}

.f-content {
  padding: clamp(8px, 2vw, 24px);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 14px;
}

.f-title {
  font-size: clamp(30px, 3.4vw, 44px);
}

.grid-section {
  padding-top: 16px;
}

.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 340px), 1fr));
  gap: 16px;
}

.post {
  background: var(--surface);
  border-radius: var(--radius-card);
  padding: 14px 14px 28px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  color: var(--ink);
  transition: background 0.2s ease;
}

.post:hover {
  background: var(--surface-hover);
  color: var(--ink);
}

.p-image {
  width: 100%;
  height: 200px;
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 4px;
}

.post > :not(.p-image) {
  margin-inline: 12px;
}

.meta {
  font-size: 14px;
  color: var(--tertiary);
}

.p-title {
  font-size: 26px;
}

.excerpt {
  font-size: 16px;
  line-height: 1.6;
  color: var(--muted);
}

.more {
  margin-top: 4px;
  font-weight: 500;
  color: var(--ink);
  text-decoration: underline;
  text-underline-offset: 3px;
}
</style>
